import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import { Base_Url } from '../../Base Url/BaseUrl';

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '');

export default function Text_Editor({ editorState, handleContentChange, maxLength = null, minHeight = '300px' }) {
  const editorInstanceRef = useRef(null);
  const [liveLength, setLiveLength] = useState(() => stripHtml(editorState).length);

  useEffect(() => {
    setLiveLength(stripHtml(editorState).length);
  }, [editorState]);

  const editorConfig = useMemo(() => ({
    minHeight,
    enter: 'DIV',
    direction: 'ltr',
    // toolbarSticky: true,
    // Upload images to the server and reference them by URL instead of embedding
    // base64 data URIs inline, which was bloating saved HTML into multi-MB payloads.
    uploader: {
      url: `${Base_Url}/upload/editorImage`,
      insertImageAsBase64URI: false,
    },
    activeButtonsInReadOnly: ['source', 'fullsize', 'print', 'about'],
    disablePlugins: ['paste', 'stat'],
    askBeforePasteHTML: true, // Prevents the link popup model from auto shutdown
    buttons:
      'bold,italic,underline,strikethrough,eraser,ul,ol,font,fontsize,paragraph,lineHeight,superscript,subscript,classSpan,file,image,video,speechRecognize,spellcheck,table,source,fullsize, about, outdent, indent, print, table, fontsize, superscript, subscript,file,cut,selectall'
  }), [minHeight]);

  // Must be stable: a new function identity here makes JoditEditor tear down and
  // rebuild the whole editor instance on every render (i.e. every keystroke).
  const setEditorInstance = useCallback((editor) => {
    editorInstanceRef.current = editor;
  }, []);

  // Live counter only; mutating the editor value mid-keystroke breaks focus/cursor in Jodit.
  const handleChange = useCallback((html) => {
    setLiveLength(stripHtml(html).length);
  }, []);

  // Jodit has no working built-in character-limit option, so the cap is enforced here,
  // once typing settles, instead of on every keystroke.
  const handleBlur = useCallback((html, event) => {
    const text = stripHtml(html);
    if (maxLength && text.length > maxLength) {
      const truncatedHtml = `<p>${text.slice(0, maxLength)}</p>`;
      if (editorInstanceRef.current) {
        editorInstanceRef.current.value = truncatedHtml;
      }
      setLiveLength(maxLength);
      handleContentChange(truncatedHtml, event);
    } else {
      handleContentChange(html, event);
    }
  }, [maxLength, handleContentChange]);

  return (
    <div>
      <JoditEditor
        value={editorState}
        editorRef={setEditorInstance}
        onChange={handleChange}
        onBlur={handleBlur}
        config={editorConfig}
      />
      {maxLength ? (
        <div className={`text-end small mt-1 ${liveLength > maxLength ? 'text-danger' : 'text-muted'}`}>
          {liveLength}/{maxLength} characters
        </div>
      ) : null}
    </div>
  );
}
